import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { UpdateStatus } from 'lavva.exalushome/build/js/Services/Updates/IUpdatesService';
import { ChildrenProps } from '../types';

type UpdateContent = {
  status: UpdateStatus;
  progress?: number;
  progressTotal?: number;
  updateNumber: number;
  updatedResourceName: string;
  availableUpdates: string[];
  description?: string;
  warning?: string;
  handleClose?: () => void;
};

type DownloadContent = {
  progress?: number;
  updatedResourceName: string;
  speedMbps: number;
};

type ExalusStateInfo = {
  message: string | ReactNode;
  loader?: boolean;
  changeInstallationButton?: boolean;
  updateContent?: UpdateContent;
  downloadContent?: DownloadContent;
  header?: string;
  buttonTitle?: string;
  handleClick?: () => void;
};

type BackdropProviderState = {
  backdropOpen: boolean;
  backdropPopupOpen: boolean;
  carouselDisabled: boolean;
  exalusPopupContent: ExalusStateInfo | null;
  turnOnBackdrop: () => void;
  turnOffBackdrop: () => void;
  turnOnBackdropPopup: (info: ExalusStateInfo) => void;
  turnOffBackdropPopup: () => void;
  preventCarouselSwipe: (disableAutoReset?: boolean) => void;
  unblockCarouselSwipe: () => void;
};

const initialState: BackdropProviderState = {
  backdropOpen: false,
  backdropPopupOpen: false,
  exalusPopupContent: null,
  carouselDisabled: false,
  turnOnBackdrop: () => null,
  turnOffBackdrop: () => null,
  turnOnBackdropPopup: () => null,
  turnOffBackdropPopup: () => null,
  preventCarouselSwipe: () => null,
  unblockCarouselSwipe: () => null,
};

export const BackdropContext = createContext<BackdropProviderState>(initialState);

export const useBackdropContext = (): BackdropProviderState => useContext(BackdropContext);

export const BackdropContextProvider: React.FC<ChildrenProps> = ({ children }) => {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(initialState.backdropOpen);
  const [backdropPopupOpen, setBackdropPopupOpen] = useState<boolean>(initialState.backdropPopupOpen);
  const [exalusPopupContent, setExalusPopupContent] = useState<ExalusStateInfo | null>(null);
  const [carouselDisabled, setCarouselDisabled] = useState<boolean>(false);
  const [carouselAutoReset, setCarouselAutoReset] = useState<boolean>(true);

  useEffect(() => {
    if (carouselDisabled && carouselAutoReset) {
      timeout.current = setTimeout(() => setCarouselDisabled(false), 800);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }

      setCarouselAutoReset(true);
    };
  }, [carouselDisabled]);

  const turnOnBackdrop = () => {
    setBackdropOpen(true);
  };

  const turnOffBackdrop = () => {
    setBackdropOpen(false);
  };

  const turnOnBackdropPopup = (info: ExalusStateInfo) => {
    setExalusPopupContent(info);
    setBackdropPopupOpen(true);
  };

  const turnOffBackdropPopup = () => {
    setExalusPopupContent(null);
    setBackdropPopupOpen(false);
  };

  const preventCarouselSwipe = (disableAutoReset?: boolean) => {
    if (disableAutoReset) setCarouselAutoReset(false);

    setCarouselDisabled(true);
  };

  const unblockCarouselSwipe = () => {
    setCarouselAutoReset(true);
    setCarouselDisabled(false);
  };

  const values: BackdropProviderState = {
    backdropOpen,
    backdropPopupOpen,
    exalusPopupContent,
    carouselDisabled,
    turnOnBackdrop,
    turnOffBackdrop,
    turnOnBackdropPopup,
    turnOffBackdropPopup,
    preventCarouselSwipe,
    unblockCarouselSwipe,
  };

  return <BackdropContext.Provider value={values}>{children}</BackdropContext.Provider>;
};
